import { EventStatus, OrgBranchEventIds } from '@types';

export const supportedFileFormats = ['JPG', 'PNG', 'PDF', 'DOC'] as const;

export type FileFormat = (typeof supportedFileFormats)[number];

export enum QuestionType {
  TEXT = 'text',
  TEXT_AREA = 'text_area',
  NUMBER = 'number',
  SINGLE_CHOICE = 'single_choice',
  MULTIPLE_CHOICE = 'multiple_choice',
  DROP_DOWN = 'drop_down',
  DATE = 'date',
  FILE_UPLOAD = 'file',
  MOBILE_NUMBER = 'mobile_number',
  EMAIL_ID = 'email_id'
}

export type QuestionMetaData = {
  [QuestionType.TEXT]: null;
  [QuestionType.TEXT_AREA]: null;
  [QuestionType.NUMBER]: null;
  [QuestionType.DATE]: null;
  [QuestionType.DROP_DOWN]: string[];
  [QuestionType.MULTIPLE_CHOICE]: string[];
  [QuestionType.SINGLE_CHOICE]: string[];
  [QuestionType.FILE_UPLOAD]: string[];
  [QuestionType.MOBILE_NUMBER]: null;
  [QuestionType.EMAIL_ID]: null;
};

export type AnswerType = {
  [QuestionType.TEXT]: string;
  [QuestionType.TEXT_AREA]: string;
  [QuestionType.NUMBER]: number;
  [QuestionType.DATE]: Date;
  [QuestionType.DROP_DOWN]: string;
  [QuestionType.MULTIPLE_CHOICE]: string[];
  [QuestionType.SINGLE_CHOICE]: string;
  [QuestionType.FILE_UPLOAD]: File;
  [QuestionType.MOBILE_NUMBER]: string;
  [QuestionType.EMAIL_ID]: string;
};

export type QuestionConstraints = {
  min?: string;
  max?: string;
};

export type Question = {
  id: string;
  question: string;
  type: QuestionType;
  required: boolean;
  questionMetaData?: QuestionMetaData[QuestionType];
  answer?: AnswerType[QuestionType];
  isDefault?: boolean;
  isDeleted?: boolean;
  displayOrder: number;
  size?: number;
  constraints?: QuestionConstraints;
};

export interface BasicDetailsFormData {
  title: string;
  imageUrl?: string;
  description: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  show_business_logo?: boolean;
  is_attendee_details_required_per_ticket?: boolean;
  allow_multiple_ticket_selection?: boolean;
  max_tickets_per_transaction?: number | null;
}

export interface OtherDetailsFormData {
  venueName: string;
  addressLine1: string;
  addressLine2?: string;
  state: string;
  city: string;
  pincode: string;
  locationLink?: string;
  additionalDocUrl?: string;
  additionalDocId?: string;
  additionalDocName?: string;
  name: string;
  email?: string;
  primaryCountryCode: string;
  primaryNumber: string;
  alternateCountryCode: string;
  alternateNumber?: string;
}

export interface Ticket {
  id?: string;
  isActive: boolean;
  name: string;
  price: string;
  description?: string;
  post_booking_instructions?: string;
  capacity?: number;
  available_capacity?: number;
  seats_booked?: number;
  subTicket?: string[];
  isSubTicketEnabled?: boolean;
  is_attendee_details_required_per_ticket?: boolean;
}

export interface SubTicket {
  id?: string;
  name: string;
  color?: string;
  to_be_deleted?: boolean;
}

export type EventSubTicketType = {
  id: string;
  name: string;
};

export interface EventData {
  basicDetails: BasicDetailsFormData;
  otherDetails: OtherDetailsFormData;
  tickets: Ticket[];
  isTicketEmailEnabled: boolean;
  questions: Question[];
  registrationCloseDate: string;
  registrationCloseTime: string;
  coupons: Coupon[];
  subTickets: EventSubTicketType[];
}

export interface EventTicket extends Ticket {
  ticketId: string;
  isActive: boolean;
  seatsBooked: number;
  capacity: number;
  subTicketCount: number;
  hasSubTicket: boolean;
  subTickets: EventSubTicket[];
}

export interface EventSubTicket {
  name: string;
  redeemedCount: number;
  redeemedPercentage: string;
}

export interface EventDetails {
  eventId: string;
  name: string;
  imageUrl: string;
  venueName?: string;
  venueCity: string;
  registrationEndDateTime: string;
  startDateTime: string;
  endDateTime: string;
  isTicketEmailEnabled: boolean;
  formLink: string;
  totalRegistrations: number;
  totalTicketSold: number;
  totalRevenue: string;
  tickets: EventTicket[];
  status: EventStatus;
  discountInsights?: DiscountInsights;
}
export interface AttendeeDetails {
  questions: Question[];
}

export interface EventFormSubmission {
  fieldName: string;
  fieldValue: string;
  fieldType: QuestionType;
  isRequired: boolean;
}
export interface DiscountInsights {
  appliedCouponCount?: number;
  discountRedeemed?: string;
  percentageRegistrationsWithDiscount?: string;
  activeCouponCount?: number;
  totalCouponCount?: number;
}
// Type for API response (CouponsTable data)
export interface Coupon {
  id: string;
  name: string;
  code: string;
  discount_amount: string | null;
  discount_percentage: string | null;
  valid_from: string | null;
  valid_to: string | null;
  show_during_registration: boolean;
  discount_amount_max: string | null;
  total_count: number | null;
  discount_eligible_amount_min: string | null;
  used_count: number;
  status: string;
  value?: string;
  valueType?: CouponValueTypeEnum;
  is_active?: boolean;
}

export interface CouponFormRequest {
  id?: string;
  name: string;
  code: string;
  discount_percentage: string | null;
  valid_from: string | null;
  valid_to: string | null;
  show_during_registration: boolean;
  discount_amount_max: string | null;
  total_count: number | null;
  discount_eligible_amount_min: string | null;
  discount_amount: string | null;
  is_active: boolean;
}

export interface CouponBulkInput {
  couponName: string;
  couponCode: string;
  couponType: CouponValueTypeEnum;
  value: string;
  minimumTicketPriceToApplyDiscount?: string;
  uptoAmountInRs?: string;
  validFrom: string;
  validUntil: string;
  usageLimit?: string;
  showDuringRegistration: 'Yes' | 'No';
}

export interface CouponsResponse {
  data: Coupon[];
  pagination: {
    page: number;
    limit: number;
    total_rows: number;
    last_page: boolean;
  };
}

export enum CouponStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  LIMIT_REACHED = 'limit_reached',
  EXPIRED = 'expired',
  SCHEDULED = 'scheduled'
}

export enum CouponValueTypeEnum {
  PERCENTAGE = 'percentage',
  AMOUNT = 'amount'
}

export enum CouponActionTypeEnum {
  ACTIVATE = 'activate',
  DEACTIVATE = 'deactivate'
}

export const couponsTableColumn = (isEditEvent: boolean) => [
  { Header: 'Name', accessor: 'name', width: '20%' },
  { Header: 'Code', accessor: 'code', width: '15%' },
  { Header: 'Value', accessor: 'value', width: '12%' },
  { Header: 'Validity Period', accessor: 'validityPeriod', width: '20%' },
  ...(isEditEvent
    ? [
        {
          Header: 'Status',
          accessor: 'status',
          width: '15%'
        }
      ]
    : []),
  { Header: 'Usage', accessor: 'usage', width: '12%' },
  { Header: 'Actions', accessor: 'actions' }
];

export enum CouponFormFields {
  COUPON_NAME = 'couponName',
  COUPON_CODE = 'couponCode',
  COUPON_TYPE = 'couponType',
  PERCENTAGE = 'percentage',
  AMOUNT = 'amount',
  MINIMUM_TICKET_PRICE = 'minimumTicketPrice',
  MAXIMUM_DISCOUNT_AMOUNT = 'maximumDiscountAmount',
  VALID_FROM = 'validFrom',
  VALID_UNTIL = 'validUntil',
  USAGE_LIMIT = 'usageLimit',
  SHOW_DURING_REGISTRATION = 'showDuringRegistration'
}

export interface CouponFormData {
  id?: string;
  couponName: string;
  couponCode: string;
  couponType: 'percent' | 'amount';
  percentage: number;
  amount: number;
  minimumTicketPrice: number;
  maximumDiscountAmount: number;
  validFrom?: string | null;
  validUntil?: string | null;
  usageLimit: string;
  showDuringRegistration: boolean;
  isActive: boolean;
}

export type ICouponFormFieldsErrors = { [key in CouponFormFields]?: string };

export type ICouponFormFieldsValues = CouponFormData[keyof CouponFormData];

export type IHandleCouponFieldValueChange = (
  field: CouponFormFields,
  value: ICouponFormFieldsValues
) => void;

export type AttendeeTransferDetails = {
  from_name: string;
  from_mobile: string;
  from_booking_id: string;
  transferred_at: string;
  note: string;
  transferred_by_name: string;
};

export type EventAttendeeCheckInStatus =
  | 'already_checked_in'
  | 'not_checked_in'
  | 'cancelled';

export type EventAttendeeRow = {
  id: number;
  name: string;
  mobile: string;
  display_id: string;
  ticket_name: string;
  payment_status: string;
  check_in_status: EventAttendeeCheckInStatus;
  paid_amount: number;
  transfer_details: AttendeeTransferDetails | null;
  discount_details?: { discount_price?: number };
  paid_amount_breakdown?: {
    ticket_price?: number;
    discount_amount?: number;
    total_gst_amount?: number;
    paid_amount?: number;
  };
  sub_ticket_details?: {
    sub_ticket_name: string;
    is_checked_in: boolean;
  }[];
};

export type EventAttendeeListResponse = {
  data: EventAttendeeRow[];
  pagination: { total_rows: number };
};

export type TransferTicketPayload = {
  params: OrgBranchEventIds & { attendeeId: number };
  body: {
    name: string;
    mobile: string;
    country_code: string;
    note: string;
    email?: string;
  };
};
